/**
 * The `{{Property}}` half of the expression language — the wizard's twin of
 * api/Generation/ExpressionResolver.ResolveReferences, so a reference default
 * fills in as you type rather than only at generation time.
 *
 * The cases live in tests/fixtures/reference-defaults.json and are run by both
 * implementations. Add a case there before changing either.
 */

import { REFERENCE } from './defaultValueRules';

/** Mirrors api/Generation/Transforms.cs. An unknown name passes the value through. */
const TRANSFORMS: Record<string, (value: string) => string> = {
  lower: (value) => value.toLowerCase(),
  upper: (value) => value.toUpperCase(),
  titlecase: (value) => value.replace(/(?<![\p{L}\p{N}])\p{L}/gu, (letter) => letter.toUpperCase()),
  trim: (value) => value.trim()
};

function applyTransforms(value: string, chain: string): string {
  return chain
    .split('>')
    .map((name) => name.trim())
    .filter(Boolean)
    .reduce((current, name) => (TRANSFORMS[name.toLowerCase()] ?? ((v: string) => v))(current), value);
}

/**
 * The twin of api/Generation/PickedRecord.Parse. A picked person, company or
 * project travels as JSON, so its fields are addressable; anything else is a
 * plain value. Nested objects, arrays and nulls flatten to empty — Templater
 * cannot address them either.
 */
export function parsePickedRecord(value: string): Record<string, string> | null {
  if (!value.trimStart().startsWith('{')) return null;

  let parsed: unknown;
  try {
    parsed = JSON.parse(value);
  } catch {
    return null;
  }

  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return null;

  const fields: Record<string, string> = {};
  for (const [name, field] of Object.entries(parsed as Record<string, unknown>)) {
    fields[name.toLowerCase()] =
      typeof field === 'string' ? field : typeof field === 'number' || typeof field === 'boolean' ? String(field) : '';
  }

  return fields;
}

/** An unknown property or field resolves to empty, so callers can fall back. */
function referenced(token: string, values: Record<string, string>): string {
  const direct = lookup(token, values);
  if (direct !== undefined) return direct;

  const dot = token.indexOf('.');
  if (dot < 0) return '';

  const record = parsePickedRecord(lookup(token.slice(0, dot), values) ?? '');
  return record?.[token.slice(dot + 1).toLowerCase()] ?? '';
}

/** Case-insensitive, matching the OrdinalIgnoreCase dictionary the server resolves against. */
function lookup(token: string, values: Record<string, string>): string | undefined {
  if (values[token] !== undefined) return values[token];

  const match = Object.keys(values).find((key) => key.toLowerCase() === token.toLowerCase());
  return match === undefined ? undefined : values[match];
}

/** Resolves `{{Property}}` and `{{Property.field}}` against already-resolved values. */
export function resolveReferences(expression: string, values: Record<string, string>): string {
  if (!expression) return '';

  return expression.replace(
    new RegExp(REFERENCE.source, REFERENCE.flags),
    (_match, token: string, chain: string) => applyTransforms(referenced(token.trim(), values), chain)
  );
}

/** True when an expression has anything for `resolveReferences` to do. */
export function hasReference(expression: string): boolean {
  return expression.includes('{{');
}
